import React from 'react';
import { TrendingUp, TrendingDown, FileText, Calculator } from 'lucide-react';
import { SYSCOHADA_CLASSES } from '../../../types/dashboard-widgets';

interface MetricsWidgetProps {
  data: any;
}

export function MetricsWidget({ data }: MetricsWidgetProps) {
  const metrics = data?.metrics;
  
  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">Aucune métrique disponible</p>
      </div>
    );
  }

  const formatCurrency = (val: number): string => {
    if (Math.abs(val) >= 1_000_000) {
      return `${(val / 1_000_000).toFixed(1)}M`;
    }
    if (Math.abs(val) >= 1_000) {
      return `${(val / 1_000).toFixed(0)}K`;
    }
    return val.toLocaleString('fr-FR');
  };

  // Build metrics from SYSCOHADA classes if available
  // SYSCOHADA_CLASSES est un tableau — itérer directement sur les items
  const classMetrics = SYSCOHADA_CLASSES.map(classInfo => {
    const classNum = parseInt(classInfo.code);
    const classData = metrics[`class${classNum}`] || metrics.classes?.[classNum] || {};
    
    return {
      class: classNum,
      code: classInfo.code,
      label: classInfo.name,    // .name est le bon champ (pas .label)
      type: classInfo.type,
      balance: classData.balance || classData.total || 0,
      count: classData.count || classData.accounts || 0
    };
  }).filter(m => m.balance !== 0 || m.count > 0);

  // Global metrics
  const globalMetrics = [
    { label: 'Total Comptes', value: metrics.totalAccounts || 0, format: 'number' },
    { label: 'Écritures', value: metrics.totalEntries || 0, format: 'number' },
    { label: 'Journaux', value: metrics.totalJournals || 0, format: 'number' },
    { label: 'Dernière mise à jour', value: metrics.lastUpdate || '', format: 'date' }
  ].filter(m => m.value);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          <Calculator className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Métriques SYSCOHADA</span>
        </div>
      </div>

      {/* Classes Summary */}
      {classMetrics.length > 0 ? (
        <div className="space-y-2">
          {classMetrics.map((item) => {
            // asset / expense = colonne débit ; equity / revenue = colonne crédit
            const isDebit = item.type === 'asset' || item.type === 'expense';
            return (
              <div 
                key={item.class} 
                className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                    isDebit 
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  }`}>
                    {item.class}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.label}</p>
                    {item.count > 0 && (
                      <p className="text-xs text-muted-foreground">{item.count} comptes</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`text-sm font-semibold ${item.balance >= 0 ? 'text-foreground' : 'text-red-600'}`}>
                    {formatCurrency(item.balance)}
                  </span>
                  {item.balance > 0 ? (
                    <TrendingUp className="h-3 w-3 text-green-600" />
                  ) : item.balance < 0 ? (
                    <TrendingDown className="h-3 w-3 text-red-600" />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Global Stats Fallback */
        <div className="grid grid-cols-2 gap-2">
          {globalMetrics.map((item, idx) => (
            <div key={idx} className="p-2 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">{item.label}</p>
              <p className="text-sm font-semibold">
                {item.format === 'date' 
                  ? new Date(item.value as string).toLocaleDateString('fr-FR')
                  : item.value.toLocaleString('fr-FR')
                }
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Footer note */}
      <div className="pt-2 border-t border-border">
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <FileText className="h-3 w-3" />
          Plan comptable SYSCOHADA
        </p>
      </div>
    </div>
  );
}
